import { useContext, JSX, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { Button } from '../shared/components/button/Button';
import { LiveChat } from '../shared/components/live-chat/LiveChat';
import { TiledLink } from '../shared/components/styledComponents/tiledLink/TiledLink';
import { AuthContext } from '../shared/context/auth-context';
import { HeaderNavigation } from '../shared/headerNavigation/HeaderNavigation';
import { useUserData } from '../shared/hooks/useUserData';
import { CallbackForm } from './components/CallbackForm';
import { pamLogo, chatSvg, emailSvg, callSvg } from './images/index';
import { CONDITIONS } from './util/constants';
import './SupportServices.css';
import { getSupportPageItems } from './util/getSupportUrl';

function SupportServices(): JSX.Element {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  const { userData } = useUserData();
  const { t } = useTranslation();
  const [isLiveChatVisible, setIsLiveChatVisible] = useState(false);
  const [isCallbackFormVisible, setIsCallbackFormVisible] = useState(false);

  const eapPhoneNumber = userData?.eapPhoneNumber || '08008824102';
  const isPamUser = userData?.customFeatureClient?.toLowerCase() === 'pam';
  const pamClientId = userData?.pamClientCode;

  useEffect(() => {
    if (!isPamUser) {
      navigate('/for-you');
    }
  }, [isPamUser, navigate]);

  const handleLiveChatClick = () => {
    setIsLiveChatVisible(true);
  };

  const toggleCallbackForm = () => {
    setIsCallbackFormVisible(!isCallbackFormVisible);
  };

  const bringFocusToLiveChatButton = () => {
    const backElement = document.getElementById('live-chat-button');
    if (backElement) {
      backElement.focus();
    }
  };

  const supportPageHeader = getSupportPageItems(auth.tags?.length > 0);

  return (
    <div id="main_container" className="foryou support-services-container platform">
      <div className="wrap-screen">
        {auth.isLoggedIn && (
          <>
            <HeaderNavigation supportPageItems={supportPageHeader} />

            <div className={`support-services ${isCallbackFormVisible && 'form-visible'}`}>
              <section className="support-services__section" aria-labelledby="need-help-heading">
                <h2 id="need-help-heading" className="support-services__title">
                  {t('SS001')}
                </h2>
                <p className="support-services__title-note">{t('support.services.needToTalk')}</p>
                <p className="support-services__phone">
                  {t('SS003')}{' '}
                  <Link to={`tel:${eapPhoneNumber}`} className="support-services__link">
                    {eapPhoneNumber}
                  </Link>
                </p>
                <div
                  className="support-services__actions"
                  role="navigation"
                  aria-label={t('SS038')}
                >
                  <Button
                    id="live-chat-button"
                    className="support-services__button"
                    ariaLabel={t('SS004')}
                    onClick={handleLiveChatClick}
                    color="dark"
                    variant="secondary"
                    icon={{
                      svg: (
                        <img
                          src={chatSvg}
                          alt={t('SS004')}
                          aria-hidden="true"
                          width="25"
                          height="20"
                        />
                      ),
                    }}
                  >
                    <div>
                      <strong>{t('SS004')}</strong>
                      <div className="support-services__button-low">
                        {t('support.services.speakToTeam')}
                      </div>
                    </div>
                  </Button>
                  <Button
                    className={`support-services__button ${isCallbackFormVisible && 'selected'}`}
                    ariaLabel={t('support.services.requestCallBack')}
                    ariaPressed={isCallbackFormVisible}
                    onClick={toggleCallbackForm}
                    color="dark"
                    variant="secondary"
                    icon={{
                      svg: (
                        <img
                          src={callSvg}
                          alt={t('support.services.requestCallBack')}
                          aria-hidden="true"
                          width="25"
                          height="20"
                        />
                      ),
                    }}
                  >
                    <div>
                      <strong>{t('support.services.requestCallBack')}</strong>
                      <div className="support-services__button-low">{t('SS007')}</div>
                    </div>
                  </Button>
                  <TiledLink
                    to="mailto:counsellingteam@pamwellbeing.co.uk?subject=EAP Wellbeing App Query&body="
                    className="greyTiledLink support-services__button"
                    ariaLabel={`${t('support.services.emailPAMWellness')} - ${t('external.link.label')}`}
                    isNewTab
                  >
                    <img
                      src={emailSvg}
                      alt={t('support.services.emailPAMWellness')}
                      aria-hidden="true"
                      width="25"
                      height="20"
                    />
                    <div>
                      <strong>{t('support.services.emailPAMWellness')}</strong>
                      <div className="support-services__tiledLink-low">{t('SS009')}</div>
                    </div>
                  </TiledLink>
                </div>
                <p className="support-services__note">{t('support.services.helpline')}</p>
                <p className="support-services__note">{t('SS011')}</p>
                <img className="support-services__image" src={pamLogo} alt="PAM Wellbeing" />
              </section>
              {isCallbackFormVisible ? (
                <CallbackForm onClose={toggleCallbackForm} />
              ) : (
                <section
                  className="support-services__section support-services__section--big"
                  aria-labelledby="support-heading"
                >
                  <div className="support-services__section-inner">
                    <h2 id="support-heading" className="support-services__subtitle">
                      {t('SS012')}
                    </h2>
                    <ul
                      className="support-services__list"
                      aria-label="Our support is available for:"
                    >
                      {CONDITIONS.map((item) => (
                        <li key={item} className="support-services__list-item">
                          {t(item)}
                        </li>
                      ))}
                    </ul>
                    <p className="support-services__note">{t('SS013')}</p>
                  </div>
                </section>
              )}
            </div>
            <LiveChat
              customVariables={{ clientId: pamClientId }}
              isVisible={isLiveChatVisible}
              onClose={() => {
                setIsLiveChatVisible(false);
                bringFocusToLiveChatButton();
              }}
            />
          </>
        )}
      </div>
    </div>
  );
}

export default SupportServices;
